import { Close } from "@mui/icons-material";
import { Box, CircularProgress, Tab } from "@mui/material";
import ModalBox from "components/Modal";
import StyledButton from "components/shared/StyledButton";
import { StyledTabs } from "components/shared/StyledTabs";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "store/store";

import GeneralTab from "./GeneralTab";
import { usePerimeterOptions } from "./hooks/usePerimeterSections";
import PerimetersTab from "./PerimetersTab";
import { permissionsToUIState, uiStateToPermissions } from "./permissionMappers";
import { transformSubjectsToCategories } from "./permissionsHelpers";
import PermissionsTab from "./PermissionsTab";
import styles from "./style.module.scss";
import type { RoleData } from "./types";

interface RolePermissionsModalProps {
    open: boolean;
    onClose: () => void;
    onSave?: (roleData: RoleData) => void;
    initialData?: RoleData;
    mode?: "create" | "edit" | "view";
    isLoading?: boolean;
    isPending?: boolean;
}

const RolePermissionsModal = ({
    open,
    onClose,
    onSave,
    initialData,
    mode = "create",
    isLoading = false,
    isPending = false,
}: RolePermissionsModalProps) => {
    const { t } = useTranslation();
    const [currentTab, setCurrentTab] = useState(0);
    const [roleName, setRoleName] = useState(initialData?.name || "");
    const [roleDescription, setRoleDescription] = useState(initialData?.description || "");

    const { subjects, actions } = useAppSelector((state) => state.permissions);
    const { perimeters: perimeterOptions, isPending: isPerimetersPending } = usePerimeterOptions();

    // Transform backend subjects into UI categories
    const permissionCategories = useMemo(() => {
        return transformSubjectsToCategories(t, subjects, actions);
    }, [t, subjects, actions]);

    // Generate default perimeter permissions (all options selected)
    const defaultPerimeterPermissions = useMemo(() => {
        const defaults: { [key: string]: any[] } = {};
        perimeterOptions.forEach((perimeter) => {
            const key = `${perimeter.subject}_${perimeter.action}::${perimeter.field}_${perimeter.operator}`;
            defaults[key] = perimeter.availableOptions.map((opt) => opt.id);
        });
        return defaults;
    }, [perimeterOptions]);

    // Convert CASL permissions to UI state for checkboxes
    const [permissions, setPermissions] = useState<{ [key: string]: boolean | any[] }>(() => {
        if (initialData?.permissions && initialData.permissions.length > 0) {
            // Edit/view mode: use existing permissions
            return permissionsToUIState(initialData.permissions, permissionCategories);
        } else {
            // Create mode: start with all perimeters selected
            return defaultPerimeterPermissions;
        }
    });

    useEffect(() => {
        if (initialData) {
            setRoleName(initialData.name || "");
            setRoleDescription(initialData.description || "");
            setPermissions(permissionsToUIState(initialData.permissions || [], permissionCategories));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialData, permissionCategories]);

    // Set default perimeters only once when component mounts in create mode
    useEffect(() => {
        if (!initialData && mode === "create" && Object.keys(defaultPerimeterPermissions).length > 0) {
            setPermissions((prev) => {
                // Only set defaults if permissions are empty
                if (Object.keys(prev).length === 0) {
                    return defaultPerimeterPermissions;
                }
                return prev;
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultPerimeterPermissions]);

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setCurrentTab(newValue);
    };

    const handlePermissionChange = (permissionKey: string, value: boolean | any[]) => {
        setPermissions((prev) => ({
            ...prev,
            [permissionKey]: value,
        }));
    };

    const handleSave = () => {
        if (!onSave) return;

        const caslPermissions = uiStateToPermissions(permissions, permissionCategories);

        onSave({
            name: roleName,
            description: roleDescription,
            permissions: caslPermissions,
        });
    };

    const handleClose = () => {
        setCurrentTab(0);
        setRoleName("");
        setRoleDescription("");
        setPermissions({});
        onClose();
    };

    const isReadOnly = mode === "view";

    return (
        <ModalBox
            open={open}
            onClose={handleClose}
            mainTitle={
                mode === "create"
                    ? t("Roles.Modal.CreateTitle")
                    : mode === "view"
                      ? t("Roles.Modal.ViewTitle")
                      : t("Roles.Modal.EditTitle")
            }
            subTitle={mode === "view" ? t("Roles.Modal.ViewSubtitle") : t("Roles.Modal.CreateSubtitle")}
            showCloseButton={false}
        >
            <div className={styles["role-permissions-modal"]}>
                <button
                    onClick={handleClose}
                    className={styles["close-button"]}
                    aria-label="Close"
                    disabled={isLoading}
                >
                    <Close />
                </button>

                <div className={styles["tabs-container"]}>
                    <StyledTabs value={currentTab} onChange={handleTabChange} className={styles["tabs"]}>
                        <Tab label={t("Roles.Modal.TabGeneral")} />
                        <Tab label={t("Roles.Modal.TabScope")} />
                        <Tab label={t("Roles.Modal.TabPermissions")} />
                    </StyledTabs>

                    <div className={styles["tab-content"]}>
                        {isPending || isPerimetersPending ? (
                            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                                <CircularProgress />
                            </Box>
                        ) : (
                            <>
                                {currentTab === 0 && (
                                    <GeneralTab
                                        roleName={roleName}
                                        roleDescription={roleDescription}
                                        onRoleNameChange={setRoleName}
                                        onRoleDescriptionChange={setRoleDescription}
                                        readOnly={isReadOnly}
                                    />
                                )}

                                {currentTab === 1 && (
                                    <PerimetersTab
                                        permissions={permissions}
                                        perimeterOptions={perimeterOptions}
                                        onPermissionChange={handlePermissionChange}
                                        readOnly={isReadOnly}
                                    />
                                )}

                                {currentTab === 2 && (
                                    <PermissionsTab
                                        permissions={permissions}
                                        onPermissionChange={handlePermissionChange}
                                        readOnly={isReadOnly}
                                    />
                                )}
                            </>
                        )}
                    </div>
                </div>

                <div className={styles["modal-actions"]}>
                    {isReadOnly ? (
                        <StyledButton buttonType="primary" onClick={handleClose} disabled={isPending}>
                            {t("Roles.Modal.Close")}
                        </StyledButton>
                    ) : (
                        <>
                            <StyledButton buttonType="outlined" onClick={handleClose} disabled={isLoading || isPending}>
                                {t("Roles.Modal.Cancel")}
                            </StyledButton>
                            <StyledButton
                                buttonType="primary"
                                onClick={handleSave}
                                disabled={!roleName.trim() || isLoading || isPending}
                            >
                                {isLoading
                                    ? t("Roles.Modal.Saving")
                                    : mode === "create"
                                      ? t("Roles.Modal.CreateRole")
                                      : t("Roles.Modal.Save")}
                            </StyledButton>
                        </>
                    )}
                </div>
            </div>
        </ModalBox>
    );
};

export default RolePermissionsModal;
